import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Stack, Typography, Grid } from '@mui/material';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

export default function ForecastGraph({ combinedData, days }) {
	// Find the index where historical data ends
	const data = [...combinedData.initial, ...combinedData[days]];

	const lastHistoricalIndex = data.findIndex((item, index, arr) =>
		!item.historical && (index === 0 || arr[index - 1].historical)
	);

	// Function to generate gradient fill for forecast area
	const getGradientFill = (ctx, colors) => {
		const gradient = ctx.createLinearGradient(0, 0, 0, 400);
		colors.forEach((color, index, { length }) => {
			gradient.addColorStop(index / (length - 1), color);
		});
		return gradient;
	}

	const chartData = {
		labels: data.map(item => item.date.toLocaleDateString()),
		datasets: [
			// Historical Data
			{
				label: 'Historical',
				data: data.map((item, index) => index <= lastHistoricalIndex ? item.value : null),
				borderColor: 'black',
				backgroundColor: 'transparent',
				tension: 0.4,
				pointRadius: 0,
			},
			{
				label: 'Forecast',
				data: data.map((item, index) => index >= lastHistoricalIndex ? item.value : null),
				borderColor: 'black',
				backgroundColor: 'transparent',
				borderDash: [5, 5],
				tension: 0.4,
				pointRadius: 0,
			},
			// 95% CI
			{
				label: '95% CI Top', // More descriptive label
				data: data.map((item, index) =>
					index >= lastHistoricalIndex
						? item.quantile_95_high // Distance from forecast
						: null
				),
				borderColor: 'transparent',
				backgroundColor: (context) => {
					if (!context.chart.chartArea) return;
					return getGradientFill(context.chart.ctx, [
						'rgba(255, 99, 132, 0.7)',
						'rgba(255, 99, 132, 0.5)'
					]);
				},
				fill: { target: '1' }, // We'll handle fill with a separate dataset below
				tension: 0.4,
				pointRadius: 0,
			},
			{
				label: '95% CI', // More descriptive label
				data: data.map((item, index) =>
					index >= lastHistoricalIndex
						? item.quantile_95_low // Distance from forecast
						: null
				),
				borderColor: 'transparent',
				backgroundColor: (context) => {
					if (!context.chart.chartArea) return;
					return getGradientFill(context.chart.ctx, [
						'rgba(255, 99, 132, 0.7)',
						'rgba(255, 99, 132, 0.5)'
					]);
				},
				fill: { target: '2' }, // We'll handle fill with a separate dataset below
				tension: 0.4,
				pointRadius: 0,
			},
			// 75% CI
			{
				label: '75% CI Top', // More descriptive label
				data: data.map((item, index) =>
					index >= lastHistoricalIndex
						? item.quantile_75_high // Distance from forecast
						: null
				),
				borderColor: 'transparent',
				backgroundColor: (context) => {
					if (!context.chart.chartArea) return;
					return getGradientFill(context.chart.ctx, [
						'rgba(255, 99, 132, 0.5)',
						'rgba(255, 99, 132, 0.3)'
					]);
				},
				fill: { target: '1' },
				tension: 0.4,
				pointRadius: 0,
			},
			{
				label: '75% CI', // More descriptive label
				data: data.map((item, index) =>
					index >= lastHistoricalIndex
						? item.quantile_75_low // Distance from forecast
						: null
				),
				borderColor: 'transparent',
				backgroundColor: (context) => {
					if (!context.chart.chartArea) return;
					return getGradientFill(context.chart.ctx, [
						'rgba(255, 99, 132, 0.5)',
						'rgba(255, 99, 132, 0.3)'
					]);
				},
				fill: { target: '1' }, // We'll handle fill with a separate dataset below
				tension: 0.4,
				pointRadius: 0,
			},
			// 50% CI
			{
				label: '50% CI Top', // More descriptive label
				data: data.map((item, index) =>
					index >= lastHistoricalIndex
						? item.quantile_50_high // Distance from forecast
						: null
				),
				borderColor: 'transparent',
				backgroundColor: (context) => {
					if (!context.chart.chartArea) return;
					return getGradientFill(context.chart.ctx, [
						'rgba(255, 99, 132, 0.5)',
						'rgba(255, 99, 132, 0.3)'
					]);
				},
				fill: { target: '1' },
				tension: 0.4,
				pointRadius: 0,
			},
			{
				label: '50% CI', // More descriptive label
				data: data.map((item, index) =>
					index >= lastHistoricalIndex
						? item.quantile_50_low // Distance from forecast
						: null
				),
				borderColor: 'transparent',
				backgroundColor: (context) => {
					if (!context.chart.chartArea) return;
					return getGradientFill(context.chart.ctx, [
						'rgba(255, 99, 132, 0.5)',
						'rgba(255, 99, 132, 0.3)'
					]);
				},
				fill: { target: '1' }, // We'll handle fill with a separate dataset below
				tension: 0.4,
				pointRadius: 0,
			},
		]
	};

	return (
		<div>
			<Line
				height={300}
				data={chartData}
				options={{
					plugins: {
						legend: {
							display: false,
						},
					},
					scales: {
						x: {
							type: 'category',
							title: {
								display: true,
								text: 'Date',
							}
						},
						y: {
							type: 'linear',
							title: {
								display: true,
								text: 'Inflation Rate',
							}
						}
					}
				}}
			/>
		</div>
	);
};